import React from "react";
import {
  emailRegex,
  FieldsValidation,
  getBaseDateFormat,
  studiesIds,
  studyStatusOptions,
  unauthorizedStudyStatus,
} from "./constants";
import { pdf } from "@react-pdf/renderer";
import moment from "moment";
import i18n from "../i18n";

export const getFormattedDate = (processDate) => {
  const year = processDate ? moment(processDate).year() : 1;
  if (year < 2000) {
    return <span>-</span>;
  }

  return moment(processDate).format(getBaseDateFormat());
};

export const smoothScrollToTop = () => {
  document.getElementById("root").scroll({
    top: 0,
    behavior: "smooth",
  });
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0 });
};

export const scrollModalToTop = () => {
  const element = document.getElementsByClassName("modal")[0];

  if (element) {
    element.scrollTop = 0;
  }
};

export const debounce = (func, wait, immediate) => {
  let timeout;
  return function () {
    let context = this,
      args = arguments;
    let later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

export const isNumeric = (value) => {
  return /^-?\d+$/.test(value);
};

export const validDateString = (date) => {
  switch (date) {
    case null:
      return false;
    case "Invalid date":
      return false;
    default:
      return true;
  }
};

export const pdfDownload = async (reportComponent, reportName) => {
  try {
    const blob = await pdf(reportComponent).toBlob();

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `${reportName}.pdf`);
    link.click();

    return Promise.resolve("success");
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getDates = (startDate, endDate) => {
  const dateArray = [];
  let currentDate = moment(startDate);
  while (currentDate <= endDate) {
    dateArray.push(moment(currentDate).format(getBaseDateFormat()));
    currentDate = moment(currentDate).add(1, "d");
  }
  return dateArray;
};

export const getUnauthorizedStudyStatus = (studyId) => {
  if (studyId.toLocaleLowerCase() === studiesIds.lcfaod302.toLowerCase()) {
    return unauthorizedStudyStatus.lcfaod302;
  }

  return [];
};

export const canShowStudyDrugMenuItem = (studyStatus) => {
  const canShowStudyStatus = [
    studyStatusOptions.TITRATION.id,
    studyStatusOptions.STABLE_TREATMENT.id,
  ];

  return canShowStudyStatus.includes(studyStatus);
};

/**
 * Get object property ignoring case types.
 * eg: obj.property = obj.Property = obj.PROPERTY
 * @param {*} obj
 * @param {*} prop
 * @returns object property value
 */
export const getPropertyNoCase = (obj, prop) => {
  if (obj && prop) {
    return obj[
      Object.keys(obj).find((key) => key.toLowerCase() === prop.toLowerCase())
    ];
  }
};

export const isEmailValid = (email) => {
  if (email.split(",").length > 1) {
    return false;
  } else {
    if (!emailRegex.test(String(email).toLowerCase())) {
      return false;
    }
    if (
      email.split("@")[0].length > FieldsValidation.email.localPart ||
      email.length > FieldsValidation.email.size
    ) {
      return false;
    }
  }
  return true;
};

export const toAlpha = (num) => {
  if (num < 1 || num > 26 || typeof num !== "number") {
    return "A";
  }
  const leveller = 64;
  //since actually A is represented by 65 and we want to represent it with one
  return String.fromCharCode(num + leveller);
};

export const updateURLQueryParams = (name, value, path, reset = false) => {
  const location = window.location;

  const params = new URLSearchParams(location.search);

  if (reset) {
    params.forEach((value, key) => {
      params.delete(key);
    });
  }

  if (params.has(name)) {
    if (value === "") {
      params.delete(name);
    } else {
      params.set(name, value);
    }
  } else if (value !== "") {
    params.append(name, value);
  }

  let anyParam = false;
  params.forEach(() => {
    anyParam = true;
  });

  window.history.pushState(
    null,
    "Rarify",
    `${path}${anyParam ? "?" : ""}${params}`
  );
};

export const sortObjectByKeys = (unordered) => {
  return Object.keys(unordered)
    .sort()
    .reduce((obj, key) => {
      obj[key] = unordered[key];
      return obj;
    }, {});
};

export const checkIfAppIsInMobile = () => {
  const mediaQuery = window.matchMedia("(max-width: 1024px)");
  if (mediaQuery.matches) {
    return true;
  }

  return false;
};

export const randomString = (length) => {
  let result = "";
  const chars = "ABCDE";
  const stringLength = chars.length;
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * stringLength));
  }
  return result;
};

export const getTextBytes = (text) => {
  return new TextEncoder().encode(text || "").length;
};

export const getTranslatedText = (text) => {
  if (!text) {
    return "";
  }

  if (
    text
      .toLowerCase()
      .startsWith(
        "your new password must be different from your previous password"
      )
  ) {
    return i18n.t("errorText.changePassword.differentPreviousPasswords");
  }

  if (text.startsWith("Your account is locked")) {
    if (text.includes("get in touch with your Study Site")) {
      return i18n.t("errorText.auth.login.locked.participant");
    }

    if (
      text.includes("Please get in touch with the") &&
      text.includes("of your Study")
    ) {
      return i18n.t("errorText.auth.login.locked.non-participant", {
        admin: text
          .split("Please get in touch with the")[1]
          .replace("of your Study", "")
          .replace(".", "")
          .trim(),
      });
    }
  }

  if (text.startsWith("Your account has been disabled")) {
    if (text.includes("get in touch with your Study Site")) {
      return i18n.t("errorText.auth.login.disabled.participant");
    }

    if (
      text.includes("Please get in touch with the") &&
      text.includes("of your Study")
    ) {
      return i18n.t("errorText.auth.login.disabled.non-participant", {
        admin: text
          .split("Please get in touch with the")[1]
          .replace("of your Study", "")
          .replace(".", "")
          .trim(),
      });
    }
  }

  if (
    text.startsWith(
      "We've re-sent you a confirmation email, please accept it before you login again"
    )
  ) {
    return i18n.t("appText.auth.resent.confirmation.email");
  }

  if (
    text.startsWith(
      "Password expired. We've sent an email to reset the password"
    )
  ) {
    return i18n.t("appText.auth.sent.reset.password.email");
  }

  if (
    text.startsWith(
      "Your password has expired. Please get in touch with your Study Site."
    )
  ) {
    return i18n.t("errorText.auth.password.expired");
  }

  if (text.startsWith("Invalid user")) {
    return i18n.t("errorText.auth.invalid.user");
  }

  return text;
};

export const getInputTimeFormat = (timeFormat) => {
  if (timeFormat === "24h") {
    return "HH:mm";
  }

  return "H:mm A";
};
